// src/services/apiClient.ts
import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosRequestHeaders,
} from 'axios';
import { refreshToken } from './authService'; // インポートはファイルの先頭に配置

// --------------------------------------------------
// 環境変数等の設定
// --------------------------------------------------
const API_BASE_URL = process.env.REACT_APP_API_URL;

// --------------------------------------------------
// axios インスタンスの生成（共通設定）
// --------------------------------------------------
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // タイムアウト時間（ms）
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * ログイン後の JWT トークンを設定するためのヘルパー関数。
 * トークンが null の場合は認証ヘッダーを削除します。
 */
export const setAuthToken = (token: string | null): void => {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
  }
};

// トークン初期化関数（クライアント側で呼び出す用）
export const initAuthToken = (): void => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    setAuthToken(token);
  }
};

/**
 * ※ axios のリクエスト設定に、カスタムプロパティを追加するための型を定義
 */
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// --------------------------------------------------
// レスポンスインターセプターの設定：アクセストークンの有効期限切れ時に自動でリフレッシュ
// --------------------------------------------------
apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as CustomAxiosRequestConfig | undefined;

    // ① 401 以外 or リフレッシュ API 自身ならそのまま reject
    if (
      !original ||
      error.response?.status !== 401 ||
      original.url?.includes('/users/token/refresh/')
    ) {
      return Promise.reject(error);
    }

    // ② まだ 1 回もリトライしていないなら…
    if (!original._retry) {
      original._retry = true;
      try {
        const newAccess = await refreshToken();
        // 直接ヘッダを書き換えないと old token が優先される場合がある
        original.headers = {
          ...(original.headers || {}),
          Authorization: `Bearer ${newAccess}`,
        } as AxiosRequestHeaders;

        return apiClient(original);
      } catch (e) {                        // リフレッシュ不可 → 強制ログアウト
        return Promise.reject(e);
      }
    }
    return Promise.reject(error);
  }
);

initAuthToken();
export default apiClient;
